import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { ReportExportService } from '../../../report-export.service';
import { toEnglishDigits } from '../../../report.utils';
import { RouteActionEvent, RouteAnalysisRow, RouteTableAction, RouteTableColumn } from './route-analysis-report.models';
import { ANALYSIS_PAGE_SIZE, ANALYSIS_PAGE_SIZES, analysisCell, analysisSearch, analysisTotal } from './route-analysis-report.utils';

@Component({ selector: 'section[app-route-analysis-table]', templateUrl: './route-analysis-table.component.html' })
export class RouteAnalysisTableComponent implements OnChanges {
  @Input() rows: RouteAnalysisRow[] = [];
  @Input() columns: RouteTableColumn[] = [];
  @Input() printColumns?: RouteTableColumn[];
  @Input() printDetails = '';
  @Input() exportColumns?: RouteTableColumn[];
  @Input() searchColumns?: RouteTableColumn[];
  @Input() searchMatcher?: (row: RouteAnalysisRow, query: string) => boolean;
  @Input() selectionField = '';
  @Input() selectedKeys: string[] = [];
  @Output() selectionChange = new EventEmitter<{ row: RouteAnalysisRow; checked: boolean }>();
  @Output() selectAllChange = new EventEmitter<boolean>();
  @Input() exportTotals = false;
  @Input() hasExternalFilters = false;
  @Output() clearExternalFilters = new EventEmitter<void>();
  @Input() actions: RouteTableAction[] = [];
  @Input() title = '';
  @Input() subtitle = '';
  @Input() fileName = '';
  @Input() tableKey = '';
  @Input() loading = false;
  @Input() hasError = false;
  @Input() errorMessage = '';
  @Input() hasRequestedData = false;
  @Input() extraSummary = '';
  @Output() retry = new EventEmitter<void>();
  @Output() rowAction = new EventEmitter<RouteActionEvent>();
  searchText = '';
  sortField = '';
  sortDescending = true;
  currentPage = 1;
  pageSize = ANALYSIS_PAGE_SIZE;
  readonly pageSizeOptions = ANALYSIS_PAGE_SIZES;
  filteredRows: RouteAnalysisRow[] = [];
  pagedRows: RouteAnalysisRow[] = [];
  constructor(private readonly exporter: ReportExportService) {}
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['tableKey']) { this.searchText = ''; this.currentPage = 1; this.sortField = ''; }
    if (changes['rows'] || changes['columns'] || changes['tableKey']) this.refresh(true);
  }
  get printId(): string { return `route-analysis-print-${this.tableKey}`; }
  footerLabelSpan(printing = false): number {
    const columns = printing ? this.printTableColumns : this.columns;
    const firstTotal = columns.findIndex(column => !!column.totalField);
    return (firstTotal === -1 ? columns.length : firstTotal) + 1;
  }
  footerColumns(printing = false): RouteTableColumn[] {
    const columns = printing ? this.printTableColumns : this.columns;
    const firstTotal = columns.findIndex(column => !!column.totalField);
    return firstTotal === -1 ? [] : columns.slice(firstTotal);
  }
  get hasSearch(): boolean { return !!this.searchText.trim(); }
  get hasOperations(): boolean { return !!this.selectionField || !!this.actions.length; }
  isSelected(row: RouteAnalysisRow): boolean { return this.selectedKeys.includes(String(row[this.selectionField])); }
  get allSelected(): boolean { return !!this.filteredRows.length && this.filteredRows.every(row => this.isSelected(row)); }
  get someSelected(): boolean { return !this.allSelected && this.filteredRows.some(row => this.isSelected(row)); }
  selectRow(row: RouteAnalysisRow, checked: boolean): void { if (!this.loading) this.selectionChange.emit({ row, checked }); }
  selectAll(checked: boolean): void { if (!this.loading) this.selectAllChange.emit(checked); }
  onRowSelection(row: RouteAnalysisRow, event: Event): void { if (event.target instanceof HTMLInputElement) this.selectRow(row, event.target.checked); }
  onAllSelection(event: Event): void { if (event.target instanceof HTMLInputElement) this.selectAll(event.target.checked); }
  get printTableColumns(): RouteTableColumn[] { return (this.printColumns ?? this.columns).filter(column => !column.action); }
  get hasFilters(): boolean { return this.hasSearch || this.hasExternalFilters; }
  get placeholder(): string { return `جستجو در جدول: ${(this.searchColumns ?? this.columns).filter(column => !column.action).slice(0, 7).map(column => column.title).join('، ')}...`; }
  get emptyTitle(): string { return this.hasFilters ? 'با این فیلترها نتیجه‌ای پیدا نشد!' : this.hasRequestedData ? 'اطلاعاتی برای نمایش وجود ندارد' : 'برای مشاهده گزارش، فیلترها را تعیین و جستجو کنید'; }
  cellValue(row: RouteAnalysisRow, column: RouteTableColumn): string | number { return analysisCell(row, column); }
  total(column: RouteTableColumn): number { return analysisTotal(this.filteredRows, column); }
  totalDuration(column: RouteTableColumn): string {
    if (column.totalDurationMinutes) {
      const minutes = Math.max(0, Math.floor(this.total(column)));
      return `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;
    }
    const seconds = Math.max(0, Math.floor(this.total(column)));
    return [Math.floor(seconds / 3600), Math.floor(seconds % 3600 / 60), seconds % 60].map(value => String(value).padStart(2, '0')).join(':');
  }
  onSearchChange(): void { if (!this.loading) this.refresh(true); }
  clearSearch(): void { if (!this.loading) { this.searchText = ''; this.refresh(true); } }
  clearFilters(): void { if (!this.loading) { this.clearSearch(); this.clearExternalFilters.emit(); } }
  sort(column: RouteTableColumn): void {
    if (this.loading || !column.sortable) return;
    this.sortDescending = this.sortField === column.field ? !this.sortDescending : true;
    this.sortField = column.field;
    this.refresh(true);
  }
  reset(): void { this.searchText = ''; this.refresh(true); }
  goToPage(page: number): void { if (!this.loading) { this.currentPage = page; this.refresh(); } }
  onPageSizeChange(size: number): void { if (!this.loading) { this.pageSize = size; this.refresh(true); } }
  trigger(event: RouteActionEvent): void { if (!this.loading) this.rowAction.emit(event); }
  exportExcel(): void {
    if (this.loading || this.hasError || !this.filteredRows.length) return;
    const columns = (this.exportColumns ?? this.columns).filter(column => !column.action);
    const rows: (string | number)[][] = this.filteredRows.map((row, index) => [index + 1, ...columns.map(column => analysisCell(row, column))]);
    if (this.exportTotals) rows.push([`تعداد کل: ${this.filteredRows.length}`, ...columns.map(column => column.totalField ? column.totalDuration ? this.totalDuration(column) : this.total(column) : '')]);
    this.exporter.exportRows(this.fileName, ['#', ...columns.map(column => column.title)], rows);
  }
  private refresh(reset = false): void {
    if (reset) this.currentPage = 1;
    const query = toEnglishDigits(this.searchText).trim().toLowerCase();
    this.filteredRows = query ? this.rows.filter(row => this.searchMatcher ? this.searchMatcher(row, query) : analysisSearch(row, this.searchColumns ?? this.columns).includes(query)) : this.rows;
    if (this.sortField) {
      this.filteredRows = [...this.filteredRows].sort((a, b) => {
        const result = toEnglishDigits(String(a[this.sortField] ?? '')).localeCompare(toEnglishDigits(String(b[this.sortField] ?? '')));
        return this.sortDescending ? -result : result;
      });
    }
    this.currentPage = Math.max(1, Math.min(this.currentPage, Math.ceil(this.filteredRows.length / this.pageSize) || 1));
    this.pagedRows = this.filteredRows.slice((this.currentPage - 1) * this.pageSize, this.currentPage * this.pageSize);
  }
}
